// Shared tooltip + period-label helpers for trend charts. Lifting
// these out keeps the line + bar variants in sync on wording and
// formatting (and saves the next maintainer from chasing two
// near-duplicates).

import dayjs from 'dayjs'

export function escapeHtml (s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Period label suitable for the bar chart's category axis (and the
 * tooltip line). Picks a friendly format per resolved frequency so
 * "Mar 2026" appears for Monthly, "Q1 2026" for Quarterly, etc.
 */
export function formatPeriodLabel (endDate, frequency) {
  if (!endDate) return ''
  const d = dayjs(endDate)
  if (!d.isValid()) return String(endDate)
  switch (frequency) {
    case 'Daily':
      return d.format('MMM D')
    case 'Weekly':
      return d.format('MMM D')
    case 'Monthly':
      return d.format('MMM YYYY')
    case 'Quarterly':
      return `Q${Math.ceil((d.month() + 1) / 3)} ${d.format('YYYY')}`
    case 'Annually':
      return d.format('YYYY')
    default:
      // Fall back to a generic format for unknown / Summary / null
      return d.format('MMM D, YYYY')
  }
}

function extractRawValue (param) {
  const point = param?.data || {}
  if (typeof point.value === 'number') return point.value
  if (typeof param?.value === 'number') return param.value
  if (Array.isArray(param?.value)) return param.value[1]
  return null
}

/**
 * ECharts tooltip formatter for trend points. Same wording across
 * line + bar so users see consistent context regardless of chart
 * type. `params.data` carries our augmented point shape
 * `{ value, endDate, periodLabel }`.
 *
 * Multi-series friendly: `trigger: 'axis'` hands us an array of params
 * (one per series at the hovered category). We render the category
 * label once at the top, then one swatch+name+value row per series.
 * Each series can declare its own value format via `seriesValueFormats`
 * (keyed by ECharts seriesId / seriesName -- whichever the chart sets);
 * falls back to the shared `valueFormat`.
 *
 * @param {(value: number, format?: string) => string} fmt -- from useFormattingContext
 * @param {string} valueFormat -- shared numeral.js format string (fallback)
 * @param {string} frequency -- resolved frequency, used for period label fallback
 * @param {Object<string, string>} [seriesValueFormats] -- optional per-series format
 */
export function buildPointTooltipFormatter (fmt, valueFormat, frequency, seriesValueFormats = null) {
  return function tooltipFormatter (params) {
    const list = Array.isArray(params) ? params : [params]
    if (!list.length) return ''
    // All params share the same axis category, so pull the label from
    // the first one; period info travels on each point's `data`.
    const head = list[0]
    const headPoint = head?.data || {}
    const headerLabel = headPoint.periodLabel || formatPeriodLabel(headPoint.endDate, frequency) || head?.name || ''

    const rows = list.map(p => {
      const seriesKey = p?.seriesId || p?.seriesName || ''
      const perSeriesFormat = (seriesValueFormats && seriesValueFormats[seriesKey]) || valueFormat
      const raw = extractRawValue(p)
      const valueText = raw == null ? '--' : fmt(raw, perSeriesFormat)
      const swatch = `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p?.color || 'currentColor'};flex-shrink:0;"></span>`
      const name = p?.seriesName || ''
      return `
        <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;">
          <div style="display:flex;align-items:center;gap:6px;min-width:0;">
            ${swatch}
            <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(name)}</span>
          </div>
          <div style="font-variant-numeric:tabular-nums;">${valueText}</div>
        </div>
      `
    }).join('')

    return `
      <div style="display:flex;flex-direction:column;gap:4px;font-size:12px;min-width:160px;">
        <div style="font-weight:600;">${escapeHtml(headerLabel)}</div>
        ${rows}
      </div>
    `
  }
}
